/* eslint-disable react/no-array-index-key */
/* eslint-disable react/prop-types */
import React from 'react'
import styled, { css } from 'styled-components'
import shortid from 'shortid'
import { fromJS } from 'immutable'
import BaseNumberFormat from 'react-number-format'
import { orderBy } from 'lodash'

import identity from 'ramda/src/identity'

import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'

import moment from '../../lib/moment'
import bindActionCreators from '../../lib/utils/bindActionCreators'

import Button from '../../lib/components/Button'
import NativeDropdown, { Dropdown } from '../../lib/components/NativeDropdown'
import DatePicker from '../../lib/components/DatePicker'
import Input, { styles as inputStyles } from '../../lib/components/Input'
import { createTable } from '../../lib/components/Table'

import Typography from '../../lib/typography'
import { momentFormatter, financialFormatter, parseFinancial } from '../../lib/utils'

import { SPACING as constants } from '../../constants'
import TrashIcon from '../../static/images/trash-hover.svg'

import { ppsAction } from '../../actions'
import { ppsSelector } from '../../selectors'

import { COST_TYPE_MAPPING } from './constants'

import arrow_down from '../../static/images/arrow-down.svg'
import arrow_up from '../../static/images/arrow-up.svg'

// prettier-ignore
const Table = createTable(css`
  grid-template-columns:
    145px
    105px
    145px
    minmax(325px, 1fr)
    145px
    170px;

  cursor: default;

  ${Dropdown.Container}::after {
    top: 52%;
    right: 8px;
  }

  ${Dropdown.Display} {
    width: 120px;
    padding-left: 8px;
    padding-right: 16px;
  }

  ${Input} {
    width: 100%;
  }

  .react-datepicker-popper {
    z-index: 99999;
  }
`)

const Trash = styled.i`
  width: 20px;
  height: 20px;
  margin-left: auto;

  background-image: url(${TrashIcon});
  background-repeat: no-repeat;
  background-position: center;

  cursor: pointer;
`

const Layout = {
  TotalCost: styled.div`
    width: 100%;
    height: 40px;
    padding: 0 ${constants.GAP};
    margin: ${constants.GAP} 0 12px;

    display: flex;
    align-items: center;

    background-color: #404040;

    > :first-child {
      margin-right: 32px;
    }

    ${Typography.Data} {
      color: #ffffff;
    }
  `,
  BreakdownCost: styled.div`
    width: 100%;
    padding: 12px 16px;
    margin-bottom: 12px;

    background-color: #404040;

    display: grid;
    grid-template-columns: repeat(4, 25%);
  `,
  CostType: styled.div`
    color: #ffffff;
    font-family: 'Gotham-Medium';
    font-size: 14px;
    line-height: 14px;

    &::before {
      content: '${props => props.label}';
      margin-right: 20px;
    }
  `,
  ScrollWrapper: styled.div`
    height: calc(100% - 312px);
    padding-bottom: 16px;
    overflow-y: scroll;
  `,
}

const Icon = styled.img`
  height: 10px;
  margin-left: 5px;
  cursor: pointer;
`

const NumberFormat = styled(BaseNumberFormat)`
  ${inputStyles}

  width: 100%;
  padding: 3px 8px 0 8px;

  font-family: 'NotoSans';
  text-align: right;
`

/**
 * @typedef {object} Props
 * @property {Array} data
 * @property {boolean} [isEditing]
 * @property {function} costFilter
 * @property {import('immutable').List} additional
 * @property {object} sums
 * @property {typeof ppsAction.setOtherCostEditing} setAdditional
 * @property {typeof ppsAction.setOtherCost} setOtherCost
 * @property {import('moment').Moment} submitMonth
 */

/**
 * @extends {React.Component<Props, *>}
 */
class OtherCost extends React.Component {
  /** @type {Props} */
  static defaultProps = {
    data: [],
    additional: fromJS([]),
    isEditing: false,
    setAdditional: () => {},
    costFilter: identity,
  }

  state = {
    sort: {
      order: false,
      column: 0,
    },
  }

  componentDidMount = async () => {
    await this.setDefaultSort()
  }

  componentDidUpdate = async prevProps => {
    const prevSubmitMonth = prevProps.submitMonth.format('MM/YYYY')
    const submitMonth = this.props.submitMonth.format('MM/YYYY')

    if (prevSubmitMonth !== submitMonth) {
      await this.setDefaultSort()
    }
  }

  setDefaultSort = async () => {
    await this.setState({ sort: { order: true, column: 5 } })
  }

  /**
   * @type {(idx: number) => (value: string) => void}
   */
  changeCostType = idx => value => {
    const { additional, setAdditional } = this.props
    setAdditional(
      additional.setIn([idx, 'type'], value).setIn([idx, 'created_date'], moment().toISOString())
    )
  }

  /**
   * @type {(idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => void}
   */
  changeSubCode = idx => e => {
    const { additional, setAdditional } = this.props
    setAdditional(
      additional
        .setIn([idx, 'sub_project_code'], e.target.value)
        .setIn([idx, 'created_date'], moment().toISOString())
    )
  }

  /**
   * @type {(idx: number) => (date: import('moment').Moment) => void}
   */
  changeDate = idx => date => {
    const { additional, setAdditional } = this.props
    setAdditional(
      additional
        .setIn([idx, 'date'], date.toISOString())
        .setIn([idx, 'created_date'], moment().toISOString())
    )
  }

  /**
   * @type {(idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => void}
   */
  changeDescription = idx => e => {
    const { additional, setAdditional } = this.props
    setAdditional(
      additional
        .setIn([idx, 'description'], e.target.value)
        .setIn([idx, 'created_date'], moment().toISOString())
    )
  }

  /**
   * @type {(idx: number) => (e: React.ChangeEvent<HTMLInputElement>) => void}
   */
  changeAmount = idx => e => {
    const { additional, setAdditional } = this.props
    setAdditional(additional.setIn([idx, 'amount'], e.target.value))
  }

  blurAmount = idx => e => {
    const { additional, setAdditional } = this.props
    const value = parseFinancial(e.target.value)
    setAdditional(
      additional
        .setIn([idx, 'amount'], Number.isNaN(value) ? 0 : Number(value.toFixed(2)))
        .setIn([idx, 'created_date'], moment().toISOString())
    )
  }

  addRow = () => {
    const { additional, setAdditional } = this.props
    setAdditional(
      additional.push(
        fromJS({
          type: '-',
          sub_project_code: '',
          date: moment().toISOString(),
          description: '',
          amount: 0,
          created_date: moment().toISOString(),
        })
      )
    )
  }

  /**
   * @type {(row: number) => () => void}
   */
  deleteRow = row => () => {
    const { additional, setAdditional } = this.props
    setAdditional(additional.delete(row))
  }

  handleClickSort = column => {
    const { sort } = this.state

    if (sort.column === column) sort.order = !sort.order
    else sort.order = true

    sort.column = column

    this.setState({ sort }, () => {
      this.sortData()
    })
  }

  arrow = column => {
    const { sort } = this.state

    if (column === sort.column) {
      return sort.order ? <Icon src={ arrow_down } alt="logo" /> : <Icon src={ arrow_up } alt="logo" />
    }

    return null
  }

  sortData = () => {
    const { data, setOtherCost, setAdditional, isEditing, additional } = this.props
    const { sort } = this.state

    const order = [
      'type',
      d =>
        (d.sub_project_code && d.sub_project_code !== '-'
          ? d.sub_project_code.replace(/-/g, '')
          : Infinity),
      d => moment(d.date).toDate(),
      d => (d.description && d.description !== '-' ? d.description : Infinity),
      'amount',
      d => moment(d.imported_date).toDate(),
    ]

    const sorted = xs => orderBy(xs, order[sort.column], sort.order ? 'desc' : 'asc')

    if (isEditing) return setAdditional(fromJS(sorted(additional.toJS())))
    return setOtherCost(sorted(data))
  }

  render = () => {
    const { data, sums, isEditing, additional, costFilter } = this.props
    const totalCost = data.reduce((acc, field) => acc + field.amount, 0)
    const cost_types = Object.entries(COST_TYPE_MAPPING).map(([value, label]) => ({ value, label }))

    return (
      <React.Fragment>
        <Layout.TotalCost>
          <Typography.Data>Total Cost (฿)</Typography.Data>
          <Typography.Data>{financialFormatter(totalCost)}</Typography.Data>
        </Layout.TotalCost>
        <Layout.BreakdownCost>
          <Layout.CostType label="Other Service (฿)">
            {financialFormatter(sums.vendor)}
          </Layout.CostType>
          <Layout.CostType label="Hardware (฿)">
            {financialFormatter(sums.hardware)}
          </Layout.CostType>
          <Layout.CostType label="License (฿)">{financialFormatter(sums.license)}</Layout.CostType>
          <Layout.CostType label="Other Cost (฿)">
            {financialFormatter(sums.other_cost)}
          </Layout.CostType>
        </Layout.BreakdownCost>
        <Layout.ScrollWrapper>
          <Table.Head style={ { overflowX: 'visible', userSelect: 'none', zIndex: 1 } }>
            <Table.HeadCell onClick={ () => this.handleClickSort(0) }>
              Cost type
              {this.arrow(0)}
            </Table.HeadCell>
            <Table.HeadCell onClick={ () => this.handleClickSort(1) }>
              Sub code
              {this.arrow(1)}
            </Table.HeadCell>
            <Table.HeadCell onClick={ () => this.handleClickSort(2) }>
              Date
              {this.arrow(2)}
            </Table.HeadCell>
            <Table.HeadCell onClick={ () => this.handleClickSort(3) }>
              Description
              {this.arrow(3)}
            </Table.HeadCell>
            <Table.HeadCell onClick={ () => this.handleClickSort(4) }>
              Amount
              {this.arrow(4)}
            </Table.HeadCell>
            <Table.HeadCell onClick={ () => this.handleClickSort(5) }>
              Created date
              {this.arrow(5)}
            </Table.HeadCell>
          </Table.Head>
          {isEditing
            ? additional.map((row, idx) => (
              <Table.Row key={ idx }>
                <Table.RowCell>
                  <NativeDropdown
                    alertDash
                    options={ cost_types }
                    value={ row.get('type') }
                    onChange={ this.changeCostType(idx) }
                  />
                </Table.RowCell>
                <Table.RowCell>
                  <Input
                    type="text"
                    value={ row.get('sub_project_code') || '' }
                    onChange={ this.changeSubCode(idx) }
                  />
                </Table.RowCell>
                <Table.RowCell>
                  <DatePicker
                    dateFormat="DD/MM/YYYY"
                    selected={ moment(row.get('date')) }
                    onChange={ this.changeDate(idx) }
                  />
                </Table.RowCell>
                <Table.RowCell>
                  <Input
                    type="text"
                    value={ row.get('description') }
                    onChange={ this.changeDescription(idx) }
                  />
                </Table.RowCell>
                <Table.RowCell>
                  <NumberFormat
                    thousandSeparator
                    allowEmptyFormatting
                    fixedDecimalScale
                    decimalScale={ 2 }
                    value={ row.get('amount') }
                    onChange={ this.changeAmount(idx) }
                    onBlur={ this.blurAmount(idx) }
                  />
                </Table.RowCell>
                <Table.RowCell>
                  {momentFormatter('DD/MM/YYYY h:mma')(row.get('imported_date'))}
                  <Trash onClick={ this.deleteRow(idx) } />
                </Table.RowCell>
              </Table.Row>
            ))
            : data.filter(costFilter).map(row => (
              <Table.Row key={ shortid.generate() }>
                <Table.RowCell>{COST_TYPE_MAPPING[row.type] || '-'}</Table.RowCell>
                <Table.RowCell>{row.sub_project_code || '-'}</Table.RowCell>
                <Table.RowCell>{momentFormatter('DD/MM/YYYY')(row.date)}</Table.RowCell>
                <Table.RowCell>{row.description || '-'}</Table.RowCell>
                <Table.RowCell currency>
                  {row.amount ? financialFormatter(row.amount) : '0.00'}
                </Table.RowCell>
                <Table.RowCell>
                  {momentFormatter('DD/MM/YYYY h:mma')(row.imported_date)}
                </Table.RowCell>
              </Table.Row>
            ))}
          {isEditing && (
            <Button color="danger" style={ { margin: '16px 0 0 24px' } } onClick={ this.addRow }>
              Add Cost
            </Button>
          )}
        </Layout.ScrollWrapper>
      </React.Fragment>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  data: ppsSelector.otherCost,
  additional: ppsSelector.otherCostEditing,
  sums: ppsSelector.costSums,
  submitMonth: ppsSelector.submitMonth,
})

const mapDispatchToProps = bindActionCreators({
  setAdditional: ppsAction.setOtherCostEditing,
  setOtherCost: ppsAction.setOtherCost,
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(OtherCost)



// WEBPACK FOOTER //
// ./src/pages/project-profit-sheet/other-cost.js